// ============================================================================
// dlFilters.ts — ЕДИНАЯ ТОЧКА РЕЗОЛВА ОТБОРОВ ДИНАМИЧЕСКИХ СПИСКОВ (SSoT).
//
// Отборы дин. списка (формы объектов — CustomLayoutRenderer/MiniDynamicList,
// виджеты «Форма списка» — builtin.ListFormWidget) могут содержать
// СПЕЦ-ЗНАЧЕНИЯ-ПРИВЯЗКИ:
//   @objectId        — id текущей записи формы (новая запись = нет id);
//   @current_user    — id текущего пользователя;
//   @param.<имя>     — параметр ВИДЖЕТА (локальный);
//   @context.<имя>   — параметр КОНТЕКСТА рабочего стола (master→slave);
//   Объект.<поле> / object.<поле> — значение реквизита формы.
//
// Резолв — ЗДЕСЬ и ТОЛЬКО здесь: рендереры получают готовый результат.
//
// ДЕТЕРМИНИЗМ (ключевой инвариант): НЕЗАПОЛНЕННАЯ ПРИВЯЗКА (@objectId у новой
// записи, не заданный @param/@context, пустое поле формы) ⇒ список ЗАВЕДОМО
// ПУСТ (`empty: true`), а НЕ «отбор не задан»: показывать ВСЕ записи таблицы в
// контексте незаполненной связи — ошибка (симптом: «Задачи по заявке» в НОВОЙ
// заявке показывал все задачи базы).
// Литеральные (не привязанные) значения не меняются — пустая константа
// сохраняет прежнюю семантику «отбор не задан».
// ============================================================================

export interface DLFilter {
  field: string;
  condition: string;
  value?: string;
}

export interface DLFilterContext {
  /** id текущей записи формы (@objectId); пусто/undefined = НОВАЯ запись */
  recordId?: string;
  /** параметры виджета (@param.<имя>) */
  params?: Record<string, string>;
  /** параметры контекста рабочего стола (@context.<имя>) */
  contextParams?: Record<string, string>;
  /** значения формы (Объект.<поле> / object.<поле>) */
  formValues?: Record<string, unknown>;
  /** id текущего пользователя (@current_user) */
  userId?: string;
}

export interface ResolvedDLFilters {
  /** Отборы с развёрнутыми привязками (пустые константы — как были). */
  filters: DLFilter[];
  /** true — отбор гарантированно не находит ничего: список надо показать
   *  ПУСТЫМ, не выполняя выборку «без отбора». */
  empty: boolean;
}

/** Разворачивает одно значение: привязка → значение из контекста; иначе —
 *  литерал как есть (bound=false — семантика «не задан отбор» не меняется). */
function resolveBoundValue(raw: string, ctx: DLFilterContext): { bound: boolean; value: string } {
  if (raw === '@objectId') return { bound: true, value: String(ctx.recordId ?? '') };
  if (raw === '@current_user' || raw === '@current_user_id') return { bound: true, value: String(ctx.userId ?? '') };
  if (raw.startsWith('@param.')) {
    const name = raw.slice('@param.'.length);
    return { bound: true, value: String((ctx.params || {})[name] ?? '') };
  }
  if (raw.startsWith('@context.')) {
    const name = raw.slice('@context.'.length);
    return { bound: true, value: String((ctx.contextParams || {})[name] ?? '') };
  }
  if (raw.startsWith('Объект.') || raw.startsWith('object.')) {
    const field = raw.split('.').pop() || '';
    const v = (ctx.formValues || {})[field];
    return { bound: true, value: v === null || v === undefined ? '' : String(v) };
  }
  return { bound: false, value: raw };
}

/** ЕДИНЫЙ резолв отборов динамического списка (см. модульный комментарий). */
export function resolveDlFilters(
  filters: DLFilter[] | undefined | null,
  ctx: DLFilterContext,
): ResolvedDLFilters {
  const resolved: DLFilter[] = [];
  for (const f of filters || []) {
    if (!f || !f.field) continue;
    const bound = resolveBoundValue(String(f.value ?? ''), ctx);
    if (bound.bound && bound.value === '') {
      // Привязка не заполнена → список заведомо пуст (детерминизм).
      return { filters: [], empty: true };
    }
    resolved.push(bound.bound ? { ...f, value: bound.value } : { ...f });
  }
  return { filters: resolved, empty: false };
}
